import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Crosshair, RefreshCw } from 'lucide-react';
import Navigation from '@/components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const probColor = (p) => p >= 50 ? 'text-brand-lime' : p >= 35 ? 'text-brand-cyan' : 'text-white';

const TDZone = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/nfl/td-zone`);
      setData(res.data);
    } catch (e) {
      console.error('td zone error', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const goToPlayer = (name) => navigate(`/nfl/players?player=${encodeURIComponent(name)}`);

  const filtered = (data?.players || []).filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.team.toLowerCase() === search.toLowerCase()
  );

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
                <Crosshair className="h-8 w-8 text-brand-lime" />
                TouchDown Zone
              </h1>
              <p className="text-muted-foreground mt-1">Projected anytime touchdowns for every game — probabilities, odds & red-zone usage</p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-[#1e3a5f] text-white hover:bg-[#112240]" disabled={refreshing} data-testid="refresh-tdzone-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-40 skeleton rounded-lg" />)}</div>
          ) : !data || (!data.games?.length && !data.players?.length) ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]"><CardContent className="p-12 text-center text-muted-foreground">No TD projections available right now.</CardContent></Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="tdzone-summary">
                <Badge variant="outline" className="border-brand-lime text-brand-lime text-sm px-3 py-1">
                  Top TD chance: {data.top_prob}%
                </Badge>
                <Badge variant="outline" className="border-brand-cyan text-brand-cyan text-sm px-3 py-1">
                  {data.games_count} games
                </Badge>
                <Badge variant="outline" className="border-[#1e3a5f] text-white text-sm px-3 py-1">
                  {data.players_count} players projected
                </Badge>
                <span className="text-xs text-muted-foreground">{data.date}</span>
              </div>

              <Tabs defaultValue="games">
                <TabsList className="bg-[#0f1e36] border border-[#1e3a5f] p-1 mb-6 flex-wrap h-auto">
                  {['games', 'players'].map(t => (
                    <TabsTrigger key={t} value={t} className="data-[state=active]:bg-brand-cyan data-[state=active]:text-[#0a1628] uppercase font-bold" data-testid={`tdzone-tab-${t}`}>
                      {t}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="games">
                  <div className="grid md:grid-cols-2 gap-4">
                    {data.games.map((g, i) => (
                      <Card key={i} className="bg-[#0f1e36] border-[#1e3a5f]" data-testid={`tdzone-game-${i}`}>
                        <CardContent className="p-5">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <div className="font-heading text-lg font-bold text-white uppercase">{g.away} @ {g.home}</div>
                              <div className="text-xs text-muted-foreground">
                                {g.spread_home != null && `${g.home} ${g.spread_home > 0 ? '+' : ''}${g.spread_home}`}
                                {g.ml_home && ` • ML ${g.ml_away}/${g.ml_home}`}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className={`font-mono text-2xl font-bold ${g.total >= 48 ? 'text-brand-lime' : 'text-brand-cyan'}`}>{g.total ?? '—'}</div>
                              <div className="text-[10px] text-muted-foreground uppercase">O/U Total</div>
                            </div>
                          </div>
                          {g.implied_home != null && (
                            <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                              <span>Implied: <span className="text-white font-mono">{g.away} {g.implied_away}</span></span>
                              <span><span className="text-white font-mono">{g.home} {g.implied_home}</span></span>
                            </div>
                          )}
                          <div className="space-y-1.5">
                            {g.top_targets.map((t, ti) => (
                              <div key={ti} className="flex items-center justify-between bg-[#0a1628] rounded px-3 py-1.5">
                                <button onClick={() => goToPlayer(t.name)} className="text-sm text-white hover:text-brand-cyan font-medium text-left" data-testid={`tdzone-game-${i}-target-${ti}`}>
                                  {t.name} <span className="text-muted-foreground text-xs">{t.team}</span>
                                </button>
                                <span className="flex items-center gap-3">
                                  <span className={`font-mono text-sm font-bold ${probColor(t.prob)}`}>{t.prob}%</span>
                                  <span className="font-mono text-xs text-brand-lime w-12 text-right">{t.odds || '—'}</span>
                                </span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="players">
                  <Input
                    placeholder="Search player or team abbr..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="bg-[#0f1e36] border-[#1e3a5f] text-white mb-4 max-w-sm"
                    data-testid="tdzone-search-input"
                  />
                  <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                    <CardContent className="p-0 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[11px] uppercase text-muted-foreground border-b border-[#1e3a5f]">
                            <th className="text-left py-3 px-4">#</th>
                            <th className="text-left py-3 px-2">Player</th>
                            <th className="text-center py-3 px-2">Pos</th>
                            <th className="text-center py-3 px-2">Matchup</th>
                            <th className="text-center py-3 px-2">TD %</th>
                            <th className="text-center py-3 px-2">Odds</th>
                            <th className="text-center py-3 px-2 hidden sm:table-cell">TD/Gm L4</th>
                            <th className="text-center py-3 px-2 hidden sm:table-cell">TD/Gm Szn</th>
                            <th className="text-center py-3 px-2 hidden md:table-cell">RZ Tries L4</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.slice(0, 100).map((p, i) => (
                            <tr key={i} className="border-b border-[#1e3a5f]/50 hover:bg-[#112240] cursor-pointer" onClick={() => goToPlayer(p.name)} data-testid={`tdzone-player-${i}`}>
                              <td className="py-2 px-4 font-mono text-muted-foreground">{i + 1}</td>
                              <td className="py-2 px-2">
                                <div className="flex items-center gap-2">
                                  {p.headshot ? (
                                    <img src={p.headshot} alt={p.name} className="w-8 h-8 rounded-full bg-[#112240] object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                  ) : <span>🏈</span>}
                                  <span className="text-white font-medium hover:text-brand-cyan">{p.name}</span>
                                </div>
                              </td>
                              <td className="text-center text-muted-foreground">{p.depth || p.pos}</td>
                              <td className="text-center text-muted-foreground text-xs">{p.team} {p.is_home ? 'vs' : '@'} {p.opp}</td>
                              <td className={`text-center font-mono font-bold ${probColor(p.prob)}`}>{p.prob}%</td>
                              <td className="text-center font-mono text-brand-lime">{p.odds || '—'}</td>
                              <td className="text-center font-mono text-white hidden sm:table-cell">{p.td_l4 ?? '—'}</td>
                              <td className="text-center font-mono text-white hidden sm:table-cell">{p.td_season ?? '—'}</td>
                              <td className="text-center font-mono text-white hidden md:table-cell">{p.rz_tries_l4 ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default TDZone;
